import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

const TYPE_COLOR = {
  Task: "bg-atlantic/10 text-atlantic border-atlantic/30",
  "Follow-Up": "bg-seaamber/15 text-seaamber border-seaamber/30",
  Call: "bg-sky/15 text-atlantic border-sky/40",
  Meeting: "bg-seagreen/10 text-seagreen border-seagreen/30",
  Activity: "bg-ice text-atlantic border-sky/40",
  Renewal: "bg-navy/10 text-navy border-navy/30",
  "Policy Expiration": "bg-seared/10 text-seared border-seared/30",
  "BOI Follow-Up": "bg-seaamber/10 text-seaamber border-seaamber/30",
};

const iso = (d) => d.toISOString().slice(0, 10);
const startOfWeek = (d) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const fmtRange = (view, anchor) => anchor.toLocaleDateString(undefined, view === "day"
  ? { weekday: "long", month: "long", day: "numeric", year: "numeric" }
  : { month: "long", year: "numeric" });

export default function AgencyCalendar() {
  const nav = useNavigate();
  const [view, setView] = useState("month");
  const [anchor, setAnchor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [types, setTypes] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [mine, setMine] = useState("all");
  const [producers, setProducers] = useState([]);
  const [producer, setProducer] = useState("all");

  const range = useMemo(() => {
    if (view === "day") return [iso(anchor), iso(anchor)];
    if (view === "week") { const s = startOfWeek(anchor); return [iso(s), iso(addDays(s, 6))]; }
    if (view === "agenda") return [iso(anchor), iso(addDays(anchor, 30))];
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    return [iso(gridStart), iso(addDays(gridStart, 41))];
  }, [view, anchor]);

  useEffect(() => { api.get("/lookups/owners").then(({ data }) => setProducers(data.items || [])).catch(() => {}); }, []);

  const load = () => {
    const p = new URLSearchParams({ start: range[0], end: range[1] });
    if (typeFilter !== "all") p.set("type", typeFilter);
    if (mine === "mine") p.set("mine", "true");
    if (producer !== "all") p.set("producer", producer);
    api.get(`/agency/calendar?${p.toString()}`).then(({ data }) => { setEvents(data.events || []); setTypes(data.types || []); })
      .catch(() => toast.error("Calendar load failed"));
  };
  useEffect(load, [range[0], range[1], typeFilter, mine, producer]);

  const byDay = useMemo(() => {
    const m = {};
    for (const e of events) (m[e.date] = m[e.date] || []).push(e);
    return m;
  }, [events]);

  const openEvent = (e) => {
    if (e.resource === "policies") nav(`/policies/${e.record_id}`);
    else if (e.resource === "business_relationships") nav(`/relationships/${e.record_id}`);
    else if (e.party_id) nav(`/clients/${e.party_id}`);
    else if (e.resource === "renewals") nav(`/renewals`);
    else nav(`/${e.resource}`);
  };

  const shift = (n) => setAnchor((a) => {
    if (view === "day") return addDays(a, n);
    if (view === "week") return addDays(a, n * 7);
    if (view === "agenda") return addDays(a, n * 30);
    return new Date(a.getFullYear(), a.getMonth() + n, 1);
  });

  const EventChip = ({ e }) => (
    <button data-testid={`cal-event-${e.record_id}`} onClick={() => openEvent(e)}
      className={`w-full text-left truncate text-xs px-1.5 py-0.5 rounded-sm border ${TYPE_COLOR[e.type] || "bg-ice text-atlantic border-sky/40"} hover:opacity-80 nav-link`}>
      {e.title}
    </button>
  );

  return (
    <div className="p-6" data-testid="agency-calendar-page">
      <PageHeader title="Agency Calendar" subtitle="Every task, activity, call, renewal and follow-up in one place — each item opens its source record.">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="cal-prev" onClick={() => shift(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" data-testid="cal-today" onClick={() => setAnchor(new Date())}>Today</Button>
          <Button variant="outline" size="icon" data-testid="cal-next" onClick={() => shift(1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex rounded-sm border border-border overflow-hidden">
          {["day", "week", "month", "agenda"].map((v) => (
            <button key={v} data-testid={`cal-view-${v}`} onClick={() => setView(v)}
              className={`px-3 py-1.5 text-sm capitalize nav-link ${view === v ? "bg-navy text-white" : "bg-white text-slate hover:bg-ice"}`}>{v}</button>
          ))}
        </div>
        <div className="font-serif text-lg text-navy ml-2 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-atlantic" />{fmtRange(view, anchor)}</div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Select value={mine} onValueChange={setMine}>
            <SelectTrigger className="w-36 h-9" data-testid="cal-mine"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">All Calendars</SelectItem><SelectItem value="mine">My Calendar</SelectItem></SelectContent>
          </Select>
          <Select value={producer} onValueChange={setProducer}>
            <SelectTrigger className="w-44 h-9" data-testid="cal-producer"><SelectValue placeholder="Producer" /></SelectTrigger>
            <SelectContent className="bg-white max-h-72"><SelectItem value="all">All Staff</SelectItem>
              {producers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40 h-9" data-testid="cal-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">All Types</SelectItem>
              {(types.length ? types : Object.keys(TYPE_COLOR)).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {view === "month" && (
        <div className="bg-white border border-border rounded-sm overflow-hidden" data-testid="cal-month">
          <div className="grid grid-cols-7 bg-cloud border-b border-border">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="px-2 py-1.5 text-xs uppercase text-navy font-semibold">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {Array.from({ length: 42 }).map((_, i) => {
              const gridStart = startOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
              const day = addDays(gridStart, i);
              const key = iso(day);
              const inMonth = day.getMonth() === anchor.getMonth();
              const dayEvents = byDay[key] || [];
              return (
                <div key={key} className={`min-h-[96px] border-b border-r border-border p-1 ${inMonth ? "bg-white" : "bg-cloud/40"}`}>
                  <div className={`text-xs mb-1 ${key === iso(new Date()) ? "font-bold text-atlantic" : "text-mutedsea"}`}>{day.getDate()}</div>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 4).map((e) => <EventChip key={e.id} e={e} />)}
                    {dayEvents.length > 4 && <div className="text-[10px] text-mutedsea px-1">+{dayEvents.length - 4} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "week" && (
        <div className="grid grid-cols-7 gap-2" data-testid="cal-week">
          {Array.from({ length: 7 }).map((_, i) => {
            const day = addDays(startOfWeek(anchor), i);
            const key = iso(day);
            const dayEvents = byDay[key] || [];
            return (
              <div key={key} className="bg-white border border-border rounded-sm min-h-[220px]">
                <div className={`px-2 py-1.5 border-b border-border text-xs font-semibold ${key === iso(new Date()) ? "text-atlantic" : "text-navy"}`}>
                  {day.toLocaleDateString(undefined, { weekday: "short", day: "numeric" })}
                </div>
                <div className="p-1 space-y-1">{dayEvents.map((e) => <EventChip key={e.id} e={e} />)}</div>
              </div>
            );
          })}
        </div>
      )}

      {(view === "day" || view === "agenda") && (
        <div className="bg-white border border-border rounded-sm divide-y divide-border" data-testid="cal-agenda">
          {events.length === 0 && <div className="p-6 text-sm text-mutedsea">No events in this range.</div>}
          {events.map((e) => (
            <button key={e.id} data-testid={`cal-event-${e.record_id}`} onClick={() => openEvent(e)}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-ice text-left nav-link">
              <div className="w-24 text-xs text-mutedsea shrink-0">{e.date}</div>
              <span className={`text-[11px] px-1.5 py-0.5 rounded-sm border shrink-0 ${TYPE_COLOR[e.type] || ""}`}>{e.type}</span>
              <div className="text-sm text-slate truncate">{e.title}</div>
              <div className="ml-auto text-xs text-mutedsea shrink-0">{e.producer}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
